import { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle,
  Clock,
  ExternalLink,
  FileText,
  Loader2,
  RefreshCw,
  XCircle,
  AlertTriangle,
  Upload,
  Zap,
  Archive,
  Sparkles,
  CheckCircle2,
  Copy,
  Link,
  Database,
  FolderTree,
  Play,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { appURL } from 'shared/constants/APIconstants';
import { UPLOAD_STATUSES, FILE_STATUSES, FILE_ROW_KINDS, isMetadataExtractable } from './constants';
import { deriveMetadataStatus, getMetadataBadgeProps } from './utils';
import knowledgeService from 'shared/api/knowledge.service.api';
import autoUploadsAPI from '@/api/auto-uploads.api';
import { useToast } from '@/components/ui/use-toast';
import { MetadataExtractionDialog } from './metadata-extraction-dialog';
import { formatNumber } from 'shared/utils';

const FILES_PAGE_SIZE = 100;
const PROGRESS_POLL_INTERVAL_MS = 10000;

const STATUS_CONFIG = {
  [UPLOAD_STATUSES.CREATED]: {
    icon: Upload,
    label: 'Created',
    color: 'bg-blue-500',
    variant: 'secondary',
  },
  [UPLOAD_STATUSES.MAPPED]: {
    icon: FolderTree,
    label: 'Mapped',
    color: 'bg-indigo-500',
    variant: 'secondary',
  },
  [UPLOAD_STATUSES.PROCESSING]: {
    icon: Zap,
    label: 'Processing',
    color: 'bg-yellow-500',
    variant: 'default',
  },
  [UPLOAD_STATUSES.ZIP_UPLOAD_COMPLETE]: {
    icon: Archive,
    label: 'ZIP Extracted',
    color: 'bg-gray-800',
    variant: 'secondary',
  },
  [UPLOAD_STATUSES.EXTRACTING_METADATA]: {
    icon: Sparkles,
    label: 'Extracting Metadata',
    color: 'bg-blue-500',
    variant: 'default',
  },
  [UPLOAD_STATUSES.COMPLETED]: {
    icon: CheckCircle2,
    label: 'Completed',
    color: 'bg-green-500',
    variant: 'default',
  },
  [UPLOAD_STATUSES.FAILED]: {
    icon: XCircle,
    label: 'Failed',
    color: 'bg-red-500',
    variant: 'destructive',
  },
};

const FILE_STATUS_CONFIG = {
  [FILE_STATUSES.PENDING]: {
    icon: Clock,
    label: 'Pending',
    variant: 'secondary',
  },
  [FILE_STATUSES.SUCCESS]: {
    icon: CheckCircle,
    label: 'Success',
    variant: 'default',
  },
  [FILE_STATUSES.FAILED]: {
    icon: XCircle,
    label: 'Failed',
    variant: 'destructive',
  },
  [FILE_STATUSES.SKIPPED]: {
    icon: AlertTriangle,
    label: 'Skipped',
    variant: 'outline',
  },
  [FILE_STATUSES.DUPLICATED]: {
    icon: Copy,
    label: 'Duplicate',
    variant: 'secondary',
  },
};

export function UploadProgress({ uploadId, createdAt }) {
  const { toast } = useToast();
  const [uploadStatus, setUploadStatus] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [files, setFiles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [creatingLink, setCreatingLink] = useState({});
  const [linkCreated, setLinkCreated] = useState({});
  const [showMetadataDialog, setShowMetadataDialog] = useState(false);
  const [metadataExtractionSkipped, setMetadataExtractionSkipped] = useState(false);
  const [uploadTags, setUploadTags] = useState([]);
  const [isStarting, setIsStarting] = useState(false);

  // Fetch files data
  const fetchFiles = useCallback(
    async (showRefreshIndicator = false) => {
      if (showRefreshIndicator) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }

      try {
        const [filesResponse, uploadResponse] = await Promise.all([
          autoUploadsAPI.getUploadFiles(uploadId, { limit: FILES_PAGE_SIZE }),
          autoUploadsAPI.getUploadById(uploadId),
        ]);

        if (filesResponse.error) {
          throw new Error(filesResponse.error.message || 'Failed to fetch upload files');
        }

        if (uploadResponse.error) {
          console.error('Failed to fetch upload details:', uploadResponse.error);
        }

        const { status, error, files: fetchedFiles, summary: summaryData } = filesResponse.data;
        setUploadStatus(status);
        setUploadError(error || null);
        setFiles(fetchedFiles || []);
        setSummary(summaryData || null);
        setMetadataExtractionSkipped(uploadResponse.data?.skipMetadataExtraction || false);
        setUploadTags(uploadResponse.data?.tags || []);
      } catch (error) {
        console.error('Error fetching upload files:', error);
        toast({
          title: 'Error',
          description: 'Failed to load upload files.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [uploadId, toast],
  );

  // Initial fetch
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);

  // Polling for active uploads
  useEffect(() => {
    if (!uploadStatus) return;

    // Stop polling if metadata extraction was skipped and files are done processing
    if (metadataExtractionSkipped && uploadStatus === UPLOAD_STATUSES.ZIP_UPLOAD_COMPLETE) return;

    const isActiveStatus = [
      UPLOAD_STATUSES.CREATED,
      UPLOAD_STATUSES.MAPPED,
      UPLOAD_STATUSES.PROCESSING,
      UPLOAD_STATUSES.ZIP_UPLOAD_COMPLETE,
      UPLOAD_STATUSES.EXTRACTING_METADATA,
    ].includes(uploadStatus);

    if (!isActiveStatus) return;

    const pollInterval = setInterval(() => {
      fetchFiles(false);
    }, PROGRESS_POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [uploadStatus, metadataExtractionSkipped, fetchFiles]);

  const handleRefresh = () => {
    fetchFiles(true);
  };

  // An upload stuck in `mapped` means /start failed after mapping — nothing was
  // dispatched, so offer a manual retry
  const handleStartProcessing = async () => {
    setIsStarting(true);
    try {
      const result = await autoUploadsAPI.startUploadProcessing(uploadId, {
        skipMetadataExtraction: metadataExtractionSkipped,
      });

      if (result.error) {
        throw new Error(result.error.message || 'Failed to start processing');
      }

      toast({
        title: 'Processing Started',
        description: 'File processing has been started for this upload.',
      });
      await fetchFiles(true);
    } catch (error) {
      console.error('Error starting upload processing:', error);
      toast({
        title: 'Start Failed',
        description: error.message || 'Could not start processing. Try again.',
        variant: 'destructive',
      });
    } finally {
      setIsStarting(false);
    }
  };

  if (isLoading && !uploadStatus) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!uploadStatus) return null;

  // Category rows are structural deletes applied at mapping time — they are not
  // extracted files and the backend excludes them from completion accounting too
  const categoryRows = files.filter(f => f.kind === FILE_ROW_KINDS.CATEGORY);
  const fileRows = files.filter(f => f.kind !== FILE_ROW_KINDS.CATEGORY);
  const nonSkippedFiles = fileRows.filter(f => f.status !== FILE_STATUSES.SKIPPED);
  const skippedFiles = fileRows.filter(f => f.status === FILE_STATUSES.SKIPPED);

  const filesSummary = summary?.files ?? {
    total: fileRows.length,
    pending: nonSkippedFiles.filter(f => f.status === FILE_STATUSES.PENDING).length,
    success: nonSkippedFiles.filter(f => f.status === FILE_STATUSES.SUCCESS).length,
    failed: nonSkippedFiles.filter(f => f.status === FILE_STATUSES.FAILED).length,
    duplicated: nonSkippedFiles.filter(f => f.status === FILE_STATUSES.DUPLICATED).length,
    skipped: skippedFiles.length,
    withKnowledgeItem: nonSkippedFiles.filter(f => f.knowledgeItemId).length,
    metadataExtractable: nonSkippedFiles.filter(
      f => f.status === FILE_STATUSES.SUCCESS && isMetadataExtractable(f),
    ).length,
    metadataExtracted: nonSkippedFiles.filter(
      f => f.status === FILE_STATUSES.SUCCESS && f.metadataExtracted,
    ).length,
  };
  const categoriesSummary = summary?.categories ?? {
    total: categoryRows.length,
    success: categoryRows.filter(f => f.status === FILE_STATUSES.SUCCESS).length,
    failed: categoryRows.filter(f => f.status === FILE_STATUSES.FAILED).length,
  };

  const totalFileRowsCount = filesSummary.total;
  const totalFiles = filesSummary.total - filesSummary.skipped;
  const completedFiles = filesSummary.success + filesSummary.failed + filesSummary.duplicated;
  const successfulFiles = filesSummary.success;
  const duplicatedFiles = filesSummary.duplicated;
  const failedFiles = filesSummary.failed;
  const filesWithKnowledgeItems = filesSummary.withKnowledgeItem;
  const extractableSuccessfulCount = filesSummary.metadataExtractable;
  const successfulFilesWithMetadata = filesSummary.metadataExtracted;
  const metadataProgress =
    extractableSuccessfulCount > 0
      ? (successfulFilesWithMetadata / extractableSuccessfulCount) * 100
      : 0;
  const progressPercentage = totalFiles > 0 ? (completedFiles / totalFiles) * 100 : 0;

  const appliedCategoryChanges = categoriesSummary.success;
  const failedCategoryCount = categoriesSummary.failed;
  const failedCategoryRows = categoryRows.filter(f => f.status === FILE_STATUSES.FAILED);

  const { displayStatus: realStatus } = deriveMetadataStatus({
    status: uploadStatus,
    skipMetadataExtraction: metadataExtractionSkipped,
    hasFiles: totalFileRowsCount > 0,
    hasPending: filesSummary.pending > 0,
    totalExtractable: extractableSuccessfulCount,
    filesWithMetadata: successfulFilesWithMetadata,
  });

  const statusConfig = STATUS_CONFIG[realStatus] || STATUS_CONFIG.created;
  const StatusIcon = statusConfig.icon;

  const formatDate = dateString => {
    return new Date(dateString).toLocaleString();
  };

  const handleCreateLink = async file => {
    if (!file.knowledgeItemId || !file.binderId) {
      return;
    }

    setCreatingLink(prev => ({ ...prev, [file.id]: true }));

    try {
      const response = await knowledgeService.createKnowledgeLink({
        categoryId: file.binderId,
        data: [{ itemId: file.knowledgeItemId }],
      });

      // Check if the response indicates failure
      if (!response.success || response.error || response.data?.message) {
        const errorMessage =
          response.data?.message || response.error?.message || 'Failed to create link';

        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }

      // Mark link as created
      setLinkCreated(prev => ({ ...prev, [file.id]: true }));

      // Refresh the files list to show updated status
      await fetchFiles(true);
    } catch (error) {
      console.error('Error creating knowledge link:', error);
    } finally {
      setCreatingLink(prev => ({ ...prev, [file.id]: false }));
    }
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <StatusIcon className="h-4 w-4" />
            Upload Progress
            <Badge
              variant={statusConfig.variant}
              className={cn(statusConfig.color, 'text-white pointer-events-none text-xs')}>
              {statusConfig.label}
            </Badge>
            {uploadTags.map(tag => (
              <Badge key={tag} variant="outline" className="text-xs pointer-events-none">
                {tag}
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {uploadStatus === UPLOAD_STATUSES.MAPPED && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleStartProcessing}
                className="flex items-center gap-2 h-7"
                disabled={isStarting}>
                {isStarting ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Play className="h-3 w-3" />
                )}
                Start Processing
              </Button>
            )}
            {realStatus === UPLOAD_STATUSES.COMPLETED && filesWithKnowledgeItems > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowMetadataDialog(true)}
                className="flex items-center gap-2 h-7">
                <Database className="h-3 w-3" />
                Queue Metadata Extraction
              </Button>
            )}
            <Button
              variant="default"
              size="sm"
              onClick={handleRefresh}
              className="flex items-center gap-2 h-7"
              disabled={isRefreshing}>
              <RefreshCw className={cn('h-3 w-3', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <div>
              <span className="text-muted-foreground">Upload ID: </span>
              <span className="font-mono text-xs font-semibold">{uploadId}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Created: </span>
              <span className="font-semibold">{formatDate(createdAt)}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Files: </span>
              <span className="font-semibold">{formatNumber(totalFileRowsCount)}</span>
              {filesSummary.skipped > 0 && (
                <span className="text-xs text-muted-foreground ml-1">
                  ({formatNumber(filesSummary.skipped)} skipped)
                </span>
              )}
            </div>
            <div>
              <span className="text-muted-foreground">Metadata Extraction: </span>
              <span
                className={cn(
                  'font-semibold',
                  metadataExtractionSkipped ? 'text-orange-600' : 'text-green-600',
                )}>
                {metadataExtractionSkipped ? 'Disabled' : 'Enabled'}
              </span>
            </div>
          </div>
        </div>

        {categoriesSummary.total > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mt-2">
            <FolderTree className="h-3.5 w-3.5 flex-shrink-0" />
            <span>
              Structure changes:{' '}
              <span className="font-medium">{formatNumber(appliedCategoryChanges)} applied</span>
            </span>
            {failedCategoryCount > 0 && (
              <span
                className={cn(
                  'text-red-600 font-medium',
                  failedCategoryRows.length > 0 && 'cursor-help',
                )}
                title={
                  failedCategoryRows.length > 0
                    ? `Failed (${
                        failedCategoryRows.length === failedCategoryCount
                          ? 'all'
                          : `latest ${failedCategoryRows.length}`
                      }): ${failedCategoryRows
                        .map(f => f.externalId)
                        .filter(Boolean)
                        .join(', ')}`
                    : undefined
                }>
                · {formatNumber(failedCategoryCount)} failed
              </span>
            )}
          </div>
        )}

        {totalFiles > 0 && (
          <>
            <Separator className="my-8" />
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>File Processing</span>
                <span>{Math.round(progressPercentage)}%</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>
                  {formatNumber(completedFiles)} of {formatNumber(totalFiles)} files processed
                </span>
                <span className="flex gap-2">
                  {successfulFiles > 0 && (
                    <span className="text-green-600 font-medium">
                      {formatNumber(successfulFiles)} uploaded
                    </span>
                  )}
                  {duplicatedFiles > 0 && (
                    <span className="text-orange-600 font-medium">
                      {formatNumber(duplicatedFiles)} duplicates
                    </span>
                  )}
                  {failedFiles > 0 && (
                    <span className="text-red-600 font-medium">
                      {formatNumber(failedFiles)} failed
                    </span>
                  )}
                  {filesSummary.skipped > 0 && (
                    <span className="text-orange-600 font-medium">
                      {formatNumber(filesSummary.skipped)} skipped
                    </span>
                  )}
                </span>
              </div>
            </div>

            {!metadataExtractionSkipped && extractableSuccessfulCount > 0 && (
              <div className="space-y-2 mt-8">
                <div className="flex justify-between text-sm">
                  <span>Metadata Extraction</span>
                  <span>{Math.round(metadataProgress)}%</span>
                </div>
                <Progress value={metadataProgress} className="h-2" />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {formatNumber(successfulFilesWithMetadata)} of{' '}
                    {formatNumber(extractableSuccessfulCount)} extractable files have metadata
                    extracted
                  </span>
                  {realStatus === UPLOAD_STATUSES.COMPLETED &&
                    successfulFilesWithMetadata === extractableSuccessfulCount && (
                      <span className="text-green-600 font-medium">✓ All metadata extracted</span>
                    )}
                </div>
              </div>
            )}
          </>
        )}

        {uploadError && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md mt-8">
            <p className="text-sm text-destructive font-medium">Error</p>
            <p className="text-sm text-destructive/80">{uploadError}</p>
          </div>
        )}

        <Separator className="my-8" />

        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            <span className="text-sm font-medium">
              Extracted Files {totalFileRowsCount > 0 && `(${formatNumber(totalFileRowsCount)})`}
            </span>
            {filesSummary.total + categoriesSummary.total > files.length && (
              <span className="text-xs text-muted-foreground">
                — showing the {formatNumber(files.length)} most recent
              </span>
            )}
          </div>

          {filesSummary.total > 0 ? (
            <div className="max-h-[400px] overflow-auto">
              <div className="space-y-2">
                {nonSkippedFiles.map((file, index) => {
                  const fileStatusConfig =
                    FILE_STATUS_CONFIG[file.status] || FILE_STATUS_CONFIG.pending;
                  const FileStatusIcon = fileStatusConfig.icon;

                  // Byte-less rows (link references, deletes by externalId) have no
                  // fileName — label them by operation instead of a blank row
                  const isBytelessRow = file.fileName == null;

                  const lastSlashIndex = file.c8Path?.lastIndexOf('/') ?? -1;
                  const binderPath =
                    lastSlashIndex > 0 ? `${file.c8Path.substring(0, lastSlashIndex)}/` : '';
                  const fileName =
                    lastSlashIndex > 0
                      ? file.c8Path.substring(lastSlashIndex + 1)
                      : file.c8Path || file.fileName;

                  return (
                    <div
                      key={file.id || index}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileStatusIcon className="h-4 w-4 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          {isBytelessRow ? (
                            <div className="flex items-center gap-2 min-w-0">
                              <p className="text-sm font-medium font-mono truncate">
                                {file.externalId}
                              </p>
                              <Badge
                                variant="outline"
                                className="text-[10px] px-1.5 py-0 pointer-events-none flex-shrink-0">
                                {(file.action || (file.link ? 'link' : 'delete')).toLowerCase()}
                              </Badge>
                            </div>
                          ) : (
                            <p className="text-sm font-medium truncate">{fileName}</p>
                          )}
                          <p className="text-xs text-muted-foreground truncate">{binderPath}</p>
                          {file.error && (
                            <p className="text-xs text-destructive mt-1">{file.error}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {isBytelessRow && file.status !== FILE_STATUSES.DUPLICATED ? (
                          <Badge
                            variant={fileStatusConfig.variant}
                            className="text-xs pointer-events-none">
                            {fileStatusConfig.label}
                          </Badge>
                        ) : file.status === FILE_STATUSES.SUCCESS ? (
                          metadataExtractionSkipped && !file.metadataExtracted ? null : (
                            (() => {
                              const badgeProps = getMetadataBadgeProps(file);
                              return (
                                <Badge
                                  variant={badgeProps.variant}
                                  className={badgeProps.className}>
                                  {badgeProps.text}
                                </Badge>
                              );
                            })()
                          )
                        ) : file.status === FILE_STATUSES.DUPLICATED ? (
                          <>
                            <Badge
                              variant="secondary"
                              className="text-xs pointer-events-none bg-orange-100 text-orange-800 border-orange-200"
                              title="This file is identical to an existing file. It has been stored but linked to the existing knowledge item to avoid duplicates.">
                              <Copy className="w-3 h-3 mr-1" />
                              Duplicate
                            </Badge>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                handleCreateLink(file);
                              }}
                              disabled={file.binderContainsItem}
                              className="h-6 px-2 text-xs"
                              title={
                                linkCreated[file.id]
                                  ? 'Link already created'
                                  : 'Create a link in this binder'
                              }>
                              <Link className="h-3 w-3 mr-1" />
                              {creatingLink[file.id]
                                ? 'Creating...'
                                : file.binderContainsItem || linkCreated[file.id]
                                  ? 'Item or link exists'
                                  : 'Create Link'}
                            </Button>
                          </>
                        ) : (
                          <Badge
                            variant={fileStatusConfig.variant}
                            className="text-xs pointer-events-none">
                            {fileStatusConfig.label}
                          </Badge>
                        )}
                        {(file.status === FILE_STATUSES.SUCCESS ||
                          file.status === FILE_STATUSES.DUPLICATED) &&
                          file.knowledgeItemId && (
                            <a
                              href={`${appURL}knowledge/items/${file.knowledgeItemId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center h-6 w-6 p-0 rounded hover:bg-muted transition-colors"
                              title="View knowledge item">
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                      </div>
                    </div>
                  );
                })}
                {skippedFiles.length > 0 && (
                  <>
                    {nonSkippedFiles.length > 0 && (
                      <div className="my-4">
                        <Separator />
                        <div className="flex items-center gap-2 mt-4 mb-2">
                          <AlertTriangle className="h-4 w-4 text-orange-500" />
                          <span className="text-sm font-medium text-muted-foreground">
                            Skipped Files ({skippedFiles.length})
                          </span>
                        </div>
                      </div>
                    )}
                    {skippedFiles.map((file, index) => {
                      const fileStatusConfig = FILE_STATUS_CONFIG[FILE_STATUSES.SKIPPED];
                      const FileStatusIcon = fileStatusConfig.icon;

                      const isBytelessRow = file.fileName == null;

                      const lastSlashIndex = file.c8Path?.lastIndexOf('/') ?? -1;
                      const binderPath =
                        lastSlashIndex > 0 ? `${file.c8Path.substring(0, lastSlashIndex)}/` : '';
                      const fileName =
                        lastSlashIndex > 0
                          ? file.c8Path.substring(lastSlashIndex + 1)
                          : file.c8Path || file.fileName;

                      return (
                        <div
                          key={file.id || `skipped-${index}`}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50 opacity-60">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <FileStatusIcon className="h-4 w-4 flex-shrink-0 text-orange-500" />
                            <div className="flex-1 min-w-0">
                              {isBytelessRow ? (
                                <div className="flex items-center gap-2 min-w-0">
                                  <p className="text-sm font-medium font-mono truncate">
                                    {file.externalId}
                                  </p>
                                  <Badge
                                    variant="outline"
                                    className="text-[10px] px-1.5 py-0 pointer-events-none flex-shrink-0">
                                    {(file.action || (file.link ? 'link' : 'delete')).toLowerCase()}
                                  </Badge>
                                </div>
                              ) : (
                                <p className="text-sm font-medium truncate">{fileName}</p>
                              )}
                              <p className="text-xs text-muted-foreground truncate">{binderPath}</p>
                              {file.error && (
                                <p className="text-xs text-orange-600 mt-1">{file.error}</p>
                              )}
                            </div>
                          </div>
                          <Badge
                            variant={fileStatusConfig.variant}
                            className="text-xs pointer-events-none">
                            {fileStatusConfig.label}
                          </Badge>
                        </div>
                      );
                    })}
                  </>
                )}
              </div>
            </div>
          ) : categoriesSummary.total > 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No files to process — this upload contains structure changes only.
            </p>
          ) : (
            <div className="flex-1 flex flex-col">
              {uploadStatus === UPLOAD_STATUSES.PROCESSING ||
              uploadStatus === UPLOAD_STATUSES.CREATED ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm">Extracting files from ZIP...</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 border rounded-lg animate-pulse">
                      <div className="flex items-center gap-3 flex-1">
                        <div className="h-4 w-4 bg-muted rounded"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-muted rounded w-3/4 mb-1"></div>
                          <div className="h-3 bg-muted rounded w-1/2"></div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="h-3 bg-muted rounded w-12"></div>
                        <div className="h-5 bg-muted rounded w-16"></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>

      <MetadataExtractionDialog
        open={showMetadataDialog}
        onClose={() => {
          setShowMetadataDialog(false);
          fetchFiles(true);
        }}
        uploadData={{
          files: fileRows,
          _id: uploadId,
          status: uploadStatus,
          skipMetadataExtraction: metadataExtractionSkipped,
        }}
      />
    </Card>
  );
}
